import { localize } from "render/context/InternationalizationContext";

/**
 * Shared translations for studies
 */

const Cardio = {
  Title: localize({
    en_GB: <>Cardio Alpha</>,
    sv_SE: <>Cardio Alpha</>,
  }),
  Invitation: localize({
    en_GB: (
      <>
        We would like to ask you if you would like to participate in a research
        project. In this document you get information about the project and what
        it means to participate. Before you consent to participate, it is
        important that you carefully read through this information, and that you
        ask if anything is unclear. Participation is completely voluntary, and
        you can cancel your participation at any time, even after you have given
        consent.
      </>
    ),
    sv_SE: (
      <>
        Vi vill fråga dig om du vill delta i ett forskningsprojekt. I det här
        dokumentet får du information om projektet och om vad det innebär att
        delta. Innan du lämnar samtycket till att delta är det viktigt att du
        noga läser igenom denna information, och att du frågar om någonting är
        oklart. Du skall också veta att deltagande är helt frivilligt, och att
        du kan avbryta ditt deltagande när som helst, även efter att du skrivit
        på samtycket.
      </>
    ),
  }),
  Purpose: localize({
    en_GB: (
      <>
        Arterial stiffness is a measure of blood vessel ageing that is strongly
        linked to the risk of high blood pressure and cardiovascular disease. We
        ask you here about participation in a study where we want to evaluate a
        new technique to measure arterial stiffness, with a combination of laser
        and radar (Laser-Radar). By studying various connections and sensitivity
        to disease states in the cardiovascular system, we want to evaluate the
        potential of the technology to be used at an early and preventive stage.
        You are offered to participate in the study either as a healthy control
        or because you have high blood pressure, one known heart valve or an
        increased risk of cardiovascular disease (assessed by your doctor).
      </>
    ),
    sv_SE: (
      <>
        Artärstelhet är ett mått på blodkärlens åldrande som är starkt kopplat
        till risk förhögt blodtryck och hjärt-kärlsjukdom. Vi frågar dig här om
        deltagande i en studie där vi vill utvärdera en ny teknik att mäta
        artärstelhet på, med en kombination av laser och radar (Laser-Radar).
        Genom att studera olika samband och känslighet för sjukdomstillstånd i
        hjärt-kärlsystemet vill vi utvärdera potentialen i tekniken att användas
        i ett tidigt och förebyggande skede. Du erbjuds att delta i studien
        antingen som en frisk kontroll eller pga att du har högt blodtryck, ett
        känt klaffel på hjärtat eller en förhöjd risk för hjärt-kärlsjukdom
        (bedömt av din läkare).
      </>
    ),
  }),
  Link: localize({
    en_GB: "https://legal.cur8.co/forms/study/2023-cardio.en.html",
    sv_SE: "https://legal.cur8.co/forms/study/2023-cardio.sv.html",
  }),
};

const DermaFlow = {
  Title: localize({
    en_GB: <>DermaFlow Alpha</>,
    sv_SE: <>DermaFlow Alpha</>,
  }),
  Invitation: localize({
    en_GB: (
      <>
        We would like to ask you if you would like to participate in a research
        project. In this document you get information about the project and what
        it means to participate. Before you consent to participate, it is
        important that you carefully read through this information, and that you
        ask if anything is unclear. Participation is completely voluntary, and
        you can cancel your participation at any time, even after you have given
        consent.
      </>
    ),
    sv_SE: (
      <>
        Vi vill fråga dig om du vill delta i ett forskningsprojekt. I det här
        dokumentet får du information om projektet och om vad det innebär att
        delta. Innan du lämnar samtycke till att delta är det viktigt att du
        noga läser igenom denna information, och att du frågar om någonting är
        oklart. Du skall också veta att deltagande är helt frivilligt, och att
        du kan avbryta ditt deltagande när som helst, även efter att du skrivit
        på samtycket.
      </>
    ),
  }),
  Purpose: localize({
    en_GB: (
      <>
        This clinical trial is about evaluating the suitability of imaging
        technology with cameras, which cover almost the entire body, for
        evaluating the possibility of screening and early detection of disease,
        but also opportunities for more diagnostics already at the health center
        and better precision in any referrals. The technology is based on use of
        several different camera systems, which can capture high-resolution
        color images (visible spectrum), 3D images, and thermal images (infrared
        spectrum). By studying various connections and susceptibility to disease
        states in the skin, as well as gradients and asymmetries in collected
        image data, the potential of the imaging technique shall be evaluated.
        You are asked to participate in the study either as a healthy control or
        when you have undiagnosed skin changes on the body and seek care at our
        clinics or other specialist clinics. You may also have any of the
        following diseases: suspected skin cancer, diabetes with diabetic feet,
        acne, eczema, psoriasis, rosacea, Raynaud's, peripheral artery disease,
        other inflammatory skin disease, psoriatic arthritis.
      </>
    ),
    sv_SE: (
      <>
        Denna kliniska prövning handlar om att utvärdera lämpligheten aven
        avbildande teknik med kameror, som täcker i stort sett hela kroppen, för
        att utvärdera möjligheten till screening och tidig upptäckt av sjukdom,
        men också möjligheter till mer diagnostik redan på vårdcentralen och
        bättre precision i eventuella remisser. Tekniken baseras på användning
        av flera olika kamerasystem, som kan fånga högupplösta färgbilder
        (synligt spektrum), 3D-bilder, och värmebilder (infrarött spektrum).
        Genom att studera olika sambandoch känslighetförsjukdomstillstånd i
        huden, samt gradienter och asymmetrier i insamlad bild-data ska
        potentialen av den avbildande tekniken utvärderas. Du tillfrågas att
        delta i studien antingen som frisk kontroll eller då du har
        odiagnostiserade hudförändringar på kroppen och söker vård på våra
        klinikereller andra specialistkliniker. Du kan även ha någon av följande
        sjukdomar: misstänkt hudcancer, diabetes med diabetesfötter, akne,
        eksem, psoriasis, rosacea, Raynaud's, perifer artärsjukdom, annan
        inflammatorisk hudsjukdom, psoriasis artrit.
      </>
    ),
  }),
  Link: localize({
    en_GB: "https://legal.cur8.co/forms/study/2023-dermaflow.en.html",
    sv_SE: "https://legal.cur8.co/forms/study/2023-dermaflow.sv.html",
  }),
};

const Spectrum = {
  Title: localize({
    en_GB: <>Spectrum 1</>,
    sv_SE: <>Spectrum 1</>,
  }),
  Invitation: localize({
    en_GB: (
      <>
        We would like to ask you if you would like to participate in a research
        project. In this document you get information about the project and what
        it means to participate. Before you consent to participate, it is
        important that you carefully read through this information, and that you
        ask if anything is unclear. Participation is completely voluntary, and
        you can cancel your participation at any time, even after you have given
        consent.
      </>
    ),
    sv_SE: (
      <>
        Vi vill fråga dig om du vill delta i ett forskningsprojekt. I det här
        dokumentet får du information om projektet och om vad det innebär att
        delta. Innan du lämnar samtycke till att delta är det viktigt att du
        noga läser igenom denna information, och att du frågar om någonting är
        oklart. Du skall också veta att deltagande är helt frivilligt, och att
        du kan avbryta ditt deltagande när som helst, även efter att du skrivit
        på samtycket.
      </>
    ),
  }),
  Purpose: localize({
    en_GB: (
      <>
        Most skin conditions affect and/or are caused by the skin
        microcirculation (blood flow in the smallest vessels). The
        microcirculation in the skin is also affected by many systemic diseases,
        such as for example diabetes and autoimmune diseases. Peripheral artery
        disease (PAD) is also highly connected with the functioning of the
        microcirculation and permission. Disease in large vessels
        (macro-circulation) can also be caused by basic problems with the
        microcirculation. PAD is considered to be severely underdiagnosed, as
        well as diabetes and general cardiovascular disease are increasing due
        to lifestyle. Together they account for the highest costs in society and
        healthcare. Today there are small opportunities to discover the above
        described ailments early, and they are often discovered when the course
        of the disease has progressed quite far, and this is why the outcome for
        the patient and burden on healthcare and society becomes costly. We ask
        you about your interest in participating in a study that aims to
        evaluate the possibilities of using a non-invasive technique for
        screening, the purpose of which is to find the diseases and conditions
        described above earlier, in a sufficiently efficient manner to enable
        use within primary care.
      </>
    ),
    sv_SE: (
      <>
        De flesta hudåkommor ger påverkan på och/eller orsakas av hudens
        mikrocirkulation (blodflöde i de minsta kärlen). Mikrocirkulationen i
        huden påverkas också av många systemiska sjukdomar, såsom till exempel
        diabetes och autoimmuna sjukdomar. Sjukdom i perifera artärer (PAD) är
        också högst sammankopplat med mikrocirkulationens funktion och
        tillstånd. Sjukdom i stora kärl (makro-cirkulationen) kan också orsakas
        av grundproblem med mikrocirkulationen. PAD anses som kraftigt
        underdiagnostiserat, samt diabetes och generell hjärtkärlsjukdom ökar
        p.g.a. livsstil. Tillsammans står det för de högsta kostnaderna i
        samhället och sjukvården. I dag finns små möjligheter att upptäcka ovan
        beskrivna åkommor tidigt, och man upptäcker det ofta när
        sjukdomsförloppet har gått ganska långt, varför utfall för patient och
        belastning för sjukvården och samhället blir kostsamt. Vi frågardig om
        ditt intresse att delta i en studie som ämnar att utvärdera
        möjligheterna till att utnyttja en icke-invasiv teknik för screening, i
        syfte attförsöka hitta ovan beskrivna sjukdomar och tillstånd tidigare,
        på ett tillräckligt effektivt sätt för att möjliggöra användande inom
        primärvården.
      </>
    ),
  }),
  Link: localize({
    en_GB: "https://legal.cur8.co/forms/study/2023-spectrum.en.html",
    sv_SE: "https://legal.cur8.co/forms/study/2023-spectrum.sv.html",
  }),
};

export const TransStudies = {
  Cardio,
  DermaFlow,
  Spectrum,
};
